import { Box, ButtonGroup, Heading, Paragraph, Tag, ToggleButton } from '@zillow/constellation-10';
import { IconListBulletedOutline, IconTrendingOutline } from '@zillow/constellation-icons';
import { useMainLayout } from '../../layouts/MainPageLayout/MainLayoutContext';
import { setCheckedCompIds, toggleCompId, useCheckedCompIds } from '../../../stores/appDataStore';
import { CustomerAnalyticsPage } from '../../SharedComponents';
import { MarketCompsMapWrapper } from './MarketCompsMapWrapper';
import { MarketCompsMapControls } from './MarketCompsMapControls';
import { AverageRentCompsGraph } from './AverageRentCompsGraph';
import { CostPerSqFtCompsGraph } from './CostPerSqFtCompsGraph';
import { MarketCompsTable } from './MarketCompsTable';
import { useEffect, useMemo, useState } from 'react';
import {
  MarketAnalyticsWebApiHandler_getCompsForListing,
  MarketAnalyticsWebApiHandler_getCompsForListing_Path,
  MarketAnalyticsWebApiHandler_getNewInventoryInRadius,
  MarketAnalyticsWebApiHandler_getNewInventoryInRadius_Path,
} from '@zg-rentals/ts-customer-analytics-client';
import type { ListingCompDto } from '@zg-rentals/ts-customer-analytics-client';
import { useQuery } from '@zg-rentals/react-query5';
import { useMarketAnalyticsWebApiHandler_getMonthlyPriceHistoryForListings } from '@zg-rentals/ts-customer-analytics-client-react-query';
import type { CompetitorMapDisplayMode } from '../../SharedComponents/Map/Dots/Dots';
import { useAuth } from '../../../AuthProvider';
import {
  emitMarketCompsSelectCompetitorEvent,
  emitMarketCompsSwitchToTableViewEvent,
  emitMarketCompsSwitchToTrendsViewEvent,
  emitMarketCompsUnselectCompetitorEvent,
} from '../../../analytics/events';

const NoPropertySelectedPrompt = () => (
  <Box css={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '800', minHeight: '60vh' }}>
    <Box
      css={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '400',
        maxWidth: '560px',
        textAlign: 'center',
      }}
    >
      <img
        src="https://delivery.digitallibrary.zillowgroup.com/public/zipcode-light-spot-3000_png_Original.png"
        alt=""
        width={160}
        height={160}
      />
      <Heading level={4}>Select a property to get started</Heading>
      <Paragraph>
        Choose a multifamily property from the filter to compare it to other properties in the area.
      </Paragraph>
    </Box>
  </Box>
);

const NoCompsFoundPrompt = () => (
  <Box
    css={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '600px',
      height: '100%',
      textAlign: 'center',
      padding: '800',
    }}
  >
    <Box css={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '400', maxWidth: '400px' }}>
      <Heading level={4}>No competitors found</Heading>
      <Paragraph>
        No competitor properties found within the current search radius. Try increasing the radius using the controls on
        the left.
      </Paragraph>
    </Box>
  </Box>
);

export const MarketComps = () => {
  const { selectedFilters, filteredProperties, setBounds } = useMainLayout();
  const { user } = useAuth();
  const checkedCompIds = useCheckedCompIds();
  type AnalysisPanel = 'table' | 'trends';
  const [activePanel, setActivePanel] = useState<AnalysisPanel>('table');
  const [numBedrooms, setNumBedrooms] = useState<number>(1);
  const [radius, setRadius] = useState<number>(3);
  const [showAllComps, setShowAllComps] = useState<boolean>(true);
  const [showNewConstruction, setShowNewConstruction] = useState<boolean>(true);
  const [mapDisplayMode, setMapDisplayMode] = useState<CompetitorMapDisplayMode>('default');

  const selectedProperty = useMemo(
    () =>
      filteredProperties.find(
        (property) =>
          property.propertyDetails.listingAlias === selectedFilters.listingAlias &&
          !property.aggregateStats.isSingleFamily,
      ),
    [filteredProperties, selectedFilters.listingAlias],
  );

  const hasPropertySelected = !!selectedProperty;

  const consolidatedZrn = useMemo(() => {
    if (!selectedProperty) return undefined;
    const propertyZrn = selectedProperty?.propertyDetails.propertyZrn;
    const customerZrn = selectedProperty?.propertyDetails.customerZrn;
    return propertyZrn ? customerZrn + '-' + propertyZrn : undefined;
  }, [selectedProperty]);

  const listingAlias = selectedProperty?.propertyDetails.listingAlias;

  const {
    data: compsData,
    isLoading: isLoadingComps,
    error: compsError,
  } = useQuery({
    queryKey: [MarketAnalyticsWebApiHandler_getCompsForListing_Path, consolidatedZrn, radius, listingAlias],
    queryFn: () =>
      MarketAnalyticsWebApiHandler_getCompsForListing({
        zrnNumber: consolidatedZrn!,
        radius,
        listingAlias,
      }),
    enabled: !!consolidatedZrn,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const comps = useMemo(() => {
    if (!compsData?.comps || !selectedProperty) return compsData?.comps;

    const selectedListingAlias = selectedProperty.propertyDetails.listingAlias;
    const alreadyIncluded = compsData.comps.some((comp) => comp.listingAlias === selectedListingAlias);

    if (alreadyIncluded || selectedProperty.currentPublishedStatus !== 'Inactive') {
      return compsData.comps;
    }

    const details = selectedProperty.propertyDetails;
    const dummyComp: ListingCompDto = {
      listingAlias: details.listingAlias,
      propertyName: details.listingName,
      latitude: details.lat,
      longitude: details.lon,
      address: details.street,
      street: details.street,
      city: details.city,
      state: details.state,
      zip: details.zip,
      unit: details.unit,
      unitId: details.unitId,
      lotId: details.lotId,
      numUnits: details.numUnits,
      cityId: details.cityRegionId,
      stateId: details.stateRegionId,
      countyId: details.countyRegionId,
      zipcodeId: details.zipRegionId,
      isUserOwned: true,
      distanceMiles: 0,
      rank: 0,
      viewsRank: 99999,
      leadsRank: 99999,
      overlapPerc: 0,
      currentProductType: selectedProperty.aggregateStats.currentAdproductType,
      hasOtherProductType: false,
      overallHighPrice: 0,
      overallLowPrice: 0,
      dateUpdated: 0,
      isUnclaimed: false,
      parking: [],
      specialOfferDescription: '',
      zillowLink: '',
      zpid: 0,
      yearBuilt: 0,
      airConditioning: false,
      fitnessCenter: false,
      greenSpace: false,
      inUnitLaundry: false,
      isDisabledHousing: false,
      isIncomeRestricted: false,
      isMilitaryHousing: false,
      isSeniorHousing: false,
      isStudentHousing: false,
      petAmenities: false,
      swimmingPool: false,
      productFeatures: [],
    };

    return [dummyComp, ...compsData.comps];
  }, [compsData, selectedProperty]);

  const listingAliases = useMemo(() => comps?.map((comp) => comp.listingAlias) ?? [], [comps]);

  const { data: priceHistoryData, isLoading: isLoadingPriceHistory } =
    useMarketAnalyticsWebApiHandler_getMonthlyPriceHistoryForListings(listingAliases, {
      enabled: listingAliases.length > 0,
    });

  const priceHistoryMap = priceHistoryData?.monthlyPriceHistoryByListingAlias;

  const { data: newConstructions, isLoading: isLoadingNewConstructions } = useQuery({
    queryKey: [
      MarketAnalyticsWebApiHandler_getNewInventoryInRadius_Path,
      selectedProperty?.propertyDetails.lat,
      selectedProperty?.propertyDetails.lon,
      radius,
    ],
    queryFn: () =>
      MarketAnalyticsWebApiHandler_getNewInventoryInRadius({
        latitude: selectedProperty!.propertyDetails.lat,
        longitude: selectedProperty!.propertyDetails.lon,
        radiusMiles: radius,
      }),
    enabled: showNewConstruction && !!selectedProperty,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const handleRecenter = () => {
    if (selectedProperty) {
      const lat = selectedProperty.propertyDetails.lat;
      const lng = selectedProperty.propertyDetails.lon;
      const delta = 0.05;
      setBounds({
        northLat: lat + delta,
        southLat: lat - delta,
        eastLng: lng + delta,
        westLng: lng - delta,
      });
    }
  };

  useEffect(() => {
    if (comps && checkedCompIds.size === 0) {
      const top5ByOverlap = comps
        .filter((comp) => comp.overlapPerc != null && comp.overlapPerc > 0)
        .sort((a, b) => (b.overlapPerc ?? 0) - (a.overlapPerc ?? 0))
        .slice(0, 5)
        .map((comp) => comp.listingAlias);
      if (top5ByOverlap.length > 0) {
        setCheckedCompIds(new Set(top5ByOverlap));
      }
    }
  }, [comps, checkedCompIds]);

  const handleToggleComp = (listingAlias: string, isSelectedProperty: boolean) => {
    if (isSelectedProperty) return;
    if (user) {
      const isCurrentlyChecked = checkedCompIds.has(listingAlias);
      if (isCurrentlyChecked) {
        emitMarketCompsUnselectCompetitorEvent({ user });
      } else {
        emitMarketCompsSelectCompetitorEvent({ user });
      }
    }
    toggleCompId(listingAlias);
  };

  const allPropertiesForTimeseries = useMemo(() => {
    if (!comps || !selectedProperty) return [];

    const selectedListingAlias = selectedProperty.propertyDetails.listingAlias;
    const selectedPropertyComp = comps.find((comp) => comp.listingAlias === selectedListingAlias);
    const checkedCompetitors = comps.filter(
      (comp) => comp.listingAlias !== selectedListingAlias && checkedCompIds.has(comp.listingAlias),
    );

    return selectedPropertyComp ? [selectedPropertyComp, ...checkedCompetitors] : checkedCompetitors;
  }, [comps, checkedCompIds, selectedProperty]);

  const relativeRanks = useMemo(() => {
    if (!comps || !selectedProperty) return { viewsRankMap: new Map(), leadsRankMap: new Map() };

    const selectedListingAlias = selectedProperty.propertyDetails.listingAlias;
    const selectedPropertyComp = comps.find((comp) => comp.listingAlias === selectedListingAlias);
    const competitorComps = comps.filter((comp) => comp.listingAlias !== selectedListingAlias);
    const checkedCompsArray = competitorComps.filter((comp) => checkedCompIds.has(comp.listingAlias));

    // Include selected property in ranking calculation
    const allPropertiesForRanking = selectedPropertyComp
      ? [selectedPropertyComp, ...checkedCompsArray]
      : checkedCompsArray;

    const viewsRanked = [...allPropertiesForRanking].sort((a, b) => a.viewsRank - b.viewsRank);
    const leadsRanked = [...allPropertiesForRanking].sort((a, b) => a.leadsRank - b.leadsRank);

    const viewsRankMap = new Map<string, number>();
    const leadsRankMap = new Map<string, number>();

    viewsRanked.forEach((comp, index) => {
      viewsRankMap.set(comp.listingAlias, index + 1);
    });

    leadsRanked.forEach((comp, index) => {
      leadsRankMap.set(comp.listingAlias, index + 1);
    });

    return { viewsRankMap, leadsRankMap };
  }, [comps, checkedCompIds, selectedProperty]);

  return (
    <CustomerAnalyticsPage
      header={
        <Box css={{ display: 'inline-flex', alignItems: 'center', gap: '200' }}>
          Competitor insights
          <Tag tone="info" size="sm">
            Beta
          </Tag>
        </Box>
      }
      hideMarketSelect={true}
    >
      <Box css={{ display: 'flex', flexDirection: 'column', gap: '600', flex: 1, minHeight: 0 }}>
        {!hasPropertySelected ? (
          <NoPropertySelectedPrompt />
        ) : (
          <>
            <Box
              css={{
                position: 'relative',
                overflow: 'hidden',
                minWidth: 0,
                minHeight: '600px',
                marginInline: '-800',
                borderTop: '1px solid token(colors.border.muted)',
                borderBottom: '1px solid token(colors.border.muted)',
              }}
            >
              {!isLoadingComps && (!comps || comps.length === 0) ? (
                <NoCompsFoundPrompt />
              ) : (
                <MarketCompsMapWrapper
                  selectedProperty={selectedProperty}
                  comps={comps}
                  isLoading={isLoadingComps}
                  error={compsError}
                  checkedCompIds={checkedCompIds}
                  showAllComps={showAllComps}
                  radius={radius}
                  showRadiusCircle={true}
                  mapDisplayMode={mapDisplayMode}
                  viewsRankMap={relativeRanks.viewsRankMap}
                  leadsRankMap={relativeRanks.leadsRankMap}
                  newConstructions={newConstructions}
                  showNewConstruction={showNewConstruction}
                  onRecenter={handleRecenter}
                />
              )}
              <MarketCompsMapControls
                selectedProperty={selectedProperty}
                hasPropertySelected={hasPropertySelected}
                radius={radius}
                setRadius={setRadius}
                showAllComps={showAllComps}
                setShowAllComps={setShowAllComps}
                showNewConstruction={showNewConstruction}
                setShowNewConstruction={setShowNewConstruction}
                isLoadingNewConstructions={isLoadingNewConstructions}
                mapDisplayMode={mapDisplayMode}
                setMapDisplayMode={setMapDisplayMode}
              />
            </Box>

            <ButtonGroup aria-label="Analysis view" conjoined orientation="horizontal" role="group">
              <ToggleButton
                selected={activePanel === 'table'}
                onClick={() => {
                  if (user) emitMarketCompsSwitchToTableViewEvent({ user });
                  setActivePanel('table');
                }}
                icon={
                  <ToggleButton.Icon>
                    <IconListBulletedOutline />
                  </ToggleButton.Icon>
                }
              >
                Table
              </ToggleButton>
              <ToggleButton
                selected={activePanel === 'trends'}
                onClick={() => {
                  if (user) emitMarketCompsSwitchToTrendsViewEvent({ user });
                  setActivePanel('trends');
                }}
                icon={
                  <ToggleButton.Icon>
                    <IconTrendingOutline />
                  </ToggleButton.Icon>
                }
              >
                Trends
              </ToggleButton>
            </ButtonGroup>

            {activePanel === 'table' && (
              <MarketCompsTable
                comps={comps ?? []}
                isLoading={isLoadingComps}
                selectedProperty={selectedProperty}
                checkedCompIds={checkedCompIds}
                onToggleComp={handleToggleComp}
                viewsRankMap={relativeRanks.viewsRankMap}
                leadsRankMap={relativeRanks.leadsRankMap}
                priceHistoryMap={priceHistoryMap}
                isLoadingPriceHistory={isLoadingPriceHistory}
              />
            )}

            {activePanel === 'trends' && (
              <Box css={{ display: 'flex', flexDirection: 'column', gap: '600' }}>
                <AverageRentCompsGraph
                  selectedProperty={selectedProperty}
                  comps={allPropertiesForTimeseries}
                  numBedrooms={numBedrooms}
                  setNumBedrooms={setNumBedrooms}
                  priceHistoryMap={priceHistoryMap}
                  isLoadingPriceHistory={isLoadingPriceHistory}
                />
                <CostPerSqFtCompsGraph
                  selectedProperty={selectedProperty}
                  comps={allPropertiesForTimeseries}
                  numBedrooms={numBedrooms}
                  setNumBedrooms={setNumBedrooms}
                  priceHistoryMap={priceHistoryMap}
                  isLoadingPriceHistory={isLoadingPriceHistory}
                />
              </Box>
            )}
          </>
        )}
      </Box>
    </CustomerAnalyticsPage>
  );
};
